import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCheck, UserX, X, Clock, Trash2, Gift, CheckCircle, XCircle } from 'lucide-react';
import { fetchAllUsers, blockUser, whitelistUser, scheduleDeleteUser, forceDeleteUser, requestGiftSubscription, verifyGiftSubscription, revokeGiftSubscription } from '../services/user';
import './User.css';

const QUICK_DAY_OPTIONS = [7, 30, 90, 180, 365];

const Users = () => {
  const [passwordPop, setPasswordPop] = useState(true);
  const [password, setPassword] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const navigate = useNavigate();
  const [deletionTimeRemaining, setDeletionTimeRemaining] = useState(null);
  const [giftSubStep, setGiftSubStep] = useState(null); // 'form', 'otp', 'success', null
  const [giftDays, setGiftDays] = useState('');
  const [giftHours, setGiftHours] = useState('');
  const [giftToken, setGiftToken] = useState('');
  const [giftOtp, setGiftOtp] = useState('');
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftError, setGiftError] = useState('');
  const [giftSuccessMsg, setGiftSuccessMsg] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0);

  // OTP countdown timer
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const interval = setInterval(() => {
      setOtpCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [otpCountdown]);

  // Reset gift sub state when user changes
  useEffect(() => {
    resetGiftSubState();
  }, [selectedUser?._id]);

  const resetGiftSubState = () => {
    setGiftSubStep(null);
    setGiftDays('');
    setGiftHours('');
    setGiftLoading(false);
    setGiftError('');
    setGiftSuccessMsg('');
    setGiftToken(null);
    setGiftOtp('');
    setOtpCountdown(0);
  };

  // Live countdown timer for pending deletion users
  useEffect(() => {
    if (!selectedUser || selectedUser.status !== 'pending_deletion' || !selectedUser.deletionScheduledAt) {
      setDeletionTimeRemaining(null);
      return;
    }

    const calculateRemaining = () => {
      const now = new Date().getTime();
      const target = new Date(selectedUser.deletionScheduledAt).getTime();
      const diff = target - now;

      if (diff <= 0) {
        setDeletionTimeRemaining({ hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setDeletionTimeRemaining({ hours, minutes, seconds, expired: false });
    };

    calculateRemaining();
    const interval = setInterval(calculateRemaining, 1000);
    return () => clearInterval(interval);
  }, [selectedUser]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetchAllUsers(password);
      const fetchedUsers = response.users;
      const usersArray = Array.isArray(fetchedUsers)
        ? fetchedUsers
        : Object.values(fetchedUsers);

      if (usersArray) {
        setUsers(usersArray);
        setPasswordPop(false);
        setError('');
      }
    } catch (err) {
      setError('Invalid password or unable to fetch users.');
    }
  };

  const handleUserClick = (user) => {
    selectedUser ? setSelectedUser(null) : setSelectedUser(user);
  };

  const handleBlockClick = async (userId) => {
    try {
      const response = await blockUser({ userId, password });
      if (response.message) {
        alert(response.message);
        setSelectedUser(null);
        handlePasswordSubmit({ preventDefault: () => {} }); // Refresh list
      } else {
        alert(response.error || 'Something went wrong');
      }
    } catch (error) {
      alert('Something went wrong!');
    }
  };

  const handleWhitelistClick = async (userId) => {
    try {
      const response = await whitelistUser({ userId, password });
      if (response.message) {
        alert(response.message);
        setSelectedUser(null);
        handlePasswordSubmit({ preventDefault: () => {} }); // Refresh list
      } else {
        alert(response.error || 'Something went wrong');
      }
    } catch (error) {
      alert('Something went wrong!');
    }
  };

  const handleScheduleDeleteClick = async (userId) => {
    const confirmed = window.confirm(
      'Are you sure you want to schedule this account for deletion? The user will be logged out and their data will be hidden. They have 48 hours to log back in to cancel.'
    );
    if (!confirmed) return;

    try {
      const response = await scheduleDeleteUser({ userId, password });
      if (response.message) {
        alert(response.message);
        setSelectedUser(null);
        handlePasswordSubmit({ preventDefault: () => {} });
      } else {
        alert(response.error || 'Something went wrong');
      }
    } catch (error) {
      alert('Something went wrong!');
    }
  };

  const handleForceDeleteClick = async (userId) => {
    const confirmed = window.confirm(
      '⚠️ DANGER: Are you sure you want to PERMANENTLY delete this user? All their posts, notifications, and data will be destroyed immediately. This action CANNOT be undone.'
    );
    if (!confirmed) return;

    try {
      const response = await forceDeleteUser({ userId, password });
      if (response.message) {
        alert(response.message);
        setSelectedUser(null);
        handlePasswordSubmit({ preventDefault: () => {} });
      } else {
        alert(response.error || 'Something went wrong');
      }
    } catch (error) {
      alert('Something went wrong!');
    }
  };

  const handleGiftSubOpen = () => {
    resetGiftSubState();
    setGiftSubStep('form');
  };

  const handleGiftSubSendOtp = async () => {
    const d = giftDays === '' ? 0 : parseInt(giftDays, 10);
    const h = giftHours === '' ? 0 : parseInt(giftHours, 10);

    if (d === 0 && h === 0) {
      setGiftError('Please provide at least 1 day or 1 hour');
      return;
    }
    if (d < 0 || d > 365) {
      setGiftError('Days must be between 0 and 365');
      return;
    }
    if (h < 0 || h > 23) {
      setGiftError('Hours must be between 0 and 23');
      return;
    }

    setGiftLoading(true);
    setGiftError('');

    try {
      const response = await requestGiftSubscription({
        userId: selectedUser._id,
        days: d,
        hours: h,
        password,
      });

      if (response.giftToken) {
        setGiftToken(response.giftToken);
        setGiftSubStep('otp');
        setOtpCountdown(600); // 10 minutes
      } else {
        setGiftError(response.error || 'Failed to send OTP');
      }
    } catch (err) {
      setGiftError('Failed to send OTP. Please try again.');
    } finally {
      setGiftLoading(false);
    }
  };

  const handleGiftSubVerify = async () => {
    if (!giftOtp || giftOtp.length !== 6 || !/^\d{6}$/.test(giftOtp)) {
      setGiftError('Please enter a valid 6-digit OTP');
      return;
    }

    setGiftLoading(true);
    setGiftError('');

    try {
      const response = await verifyGiftSubscription({
        giftToken,
        otp: giftOtp,
        password,
      });

      if (response.message) {
        setGiftSuccessMsg(response.message);
        setGiftSubStep('success');
        // Refresh users list
        handlePasswordSubmit({ preventDefault: () => {} });
      } else {
        setGiftError(response.error || 'Verification failed');
      }
    } catch (err) {
      setGiftError('Verification failed. Please try again.');
    } finally {
      setGiftLoading(false);
    }
  };

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleRevokeSubscription = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to revoke the gift subscription for ${selectedUser.name || selectedUser.email}? This will immediately remove their premium access.`
    );
    if (!confirmed) return;

    const reason = window.prompt('Reason for revocation (optional):') || '';

    try {
      const response = await revokeGiftSubscription({
        userId: selectedUser._id,
        reason,
        password,
      });

      if (response.message) {
        alert(response.message);
        setSelectedUser(null);
        handlePasswordSubmit({ preventDefault: () => {} });
      } else {
        alert(response.error || 'Something went wrong');
      }
    } catch (error) {
      alert('Failed to revoke subscription');
    }
  };

  const handleClosePasswordPopup = () => {
    setPassword('');
    setError('');
    navigate(-1);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchName.toLowerCase()) &&
      user.email.toLowerCase().includes(searchEmail.toLowerCase()) &&
      user.joiningDate.includes(searchDate)
  );

  const renderStatusIcon = (status) => {
    if (status === 'blacklisted') {
      return (
        <div className="group relative flex items-center justify-center">
          <UserX size={16} className="text-red-500" />
          <span className="absolute bottom-full mb-1 hidden whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:block group-hover:opacity-100 z-10">
            Blacklisted
          </span>
        </div>
      );
    }
    if (status === 'pending_deletion') {
      return (
        <div className="group relative flex items-center justify-center">
          <Clock size={16} className="text-amber-500" />
          <span className="absolute bottom-full mb-1 hidden whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:block group-hover:opacity-100 z-10">
            Pending Deletion
          </span>
        </div>
      );
    }
    return (
      <div className="group relative flex items-center justify-center">
        <UserCheck size={16} className="text-green-500" />
        <span className="absolute bottom-full mb-1 hidden whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:block group-hover:opacity-100 z-10">
          Active
        </span>
      </div>
    );
  };

  // Gift Subscription Form Renderer
  const renderGiftSubSection = () => {
    if (giftSubStep === 'success') {
      return (
        <div className="gift-sub-success">
          <CheckCircle size={18} />
          <span>{giftSuccessMsg}</span>
        </div>
      );
    }

    if (giftSubStep === 'form') {
      return (
        <div className="gift-sub-form">
          <h3>Gift Subscription</h3>

          {/* Quick day selection */}
          <label>Select Duration</label>
          <div className="gift-sub-quick-days">
            {QUICK_DAY_OPTIONS.map((d) => (
              <button
                key={d}
                className={giftDays === d ? 'active' : ''}
                onClick={() => setGiftDays(d)}
              >
                {d} days
              </button>
            ))}
          </div>

          {/* Custom days input */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            <div style={{ flex: 1 }}>
              <label>Custom days (0–365)</label>
              <input
                type="number"
                min="0"
                max="365"
                value={giftDays}
                onChange={(e) => setGiftDays(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                placeholder="Days"
                style={{ width: '100%', marginTop: '4px' }}
              />
            </div>
            
            <div style={{ flex: 1 }}>
              <label>Hours (0–23)</label>
              <input
                type="number"
                min="0"
                max="23"
                value={giftHours}
                onChange={(e) => setGiftHours(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                placeholder="Hours"
                style={{ width: '100%', marginTop: '4px' }}
              />
            </div>
          </div>

          {giftError && <p className="gift-sub-error">{giftError}</p>}

          <div className="gift-sub-actions">
            <button
              className="gift-sub-btn-send"
              onClick={handleGiftSubSendOtp}
              disabled={giftLoading}
            >
              {giftLoading && <span className="gift-sub-loading"></span>}
              Send OTP
            </button>
            <button
              className="gift-sub-btn-cancel"
              onClick={resetGiftSubState}
              disabled={giftLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    if (giftSubStep === 'otp') {
      return (
        <div className="gift-sub-form">
          <h3>🔐 Verify OTP</h3>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
            A 6-digit OTP has been sent to the admin mobile number.
            Enter it below to activate the subscription for <strong>{giftDays} days</strong>.
          </p>

          <div className="gift-sub-otp-section">
            <label>Enter OTP</label>
            <input
              type="text"
              className="gift-sub-otp-input"
              value={giftOtp}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setGiftOtp(val);
              }}
              placeholder="000000"
              maxLength={6}
            />

            {otpCountdown > 0 && (
              <p className="gift-sub-countdown">
                OTP expires in <strong>{formatCountdown(otpCountdown)}</strong>
              </p>
            )}
            {otpCountdown === 0 && giftSubStep === 'otp' && (
              <p className="gift-sub-error">OTP has expired. Please request a new one.</p>
            )}
          </div>

          {giftError && <p className="gift-sub-error">{giftError}</p>}

          <div className="gift-sub-actions">
            <button
              className="gift-sub-btn-verify"
              onClick={handleGiftSubVerify}
              disabled={giftLoading || otpCountdown === 0}
            >
              {giftLoading && <span className="gift-sub-loading"></span>}
              Verify & Activate
            </button>
            <button
              className="gift-sub-btn-cancel"
              onClick={resetGiftSubState}
              disabled={giftLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {passwordPop && (
        <div className="password-popup">
          <h2>Enter Master Password</h2>
          <span
            className="password-popup-close"
            onClick={handleClosePasswordPopup}
            role="button"
            aria-label="Close"
          >
            ✕
          </span>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              placeholder="Master Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="submit">Submit</button>
          </form>
          {error && <p className="error-message">{error}</p>}
        </div>
      )}

      {users.length > 0 ? (
        <>
          {/* Header */}
          <div className="users-header">
            <button onClick={() => navigate(-1)} className="users-back-btn">
              <ArrowLeft size={16} />
              Back
            </button>
            <h1>All users</h1>
          </div>
          {/* Search Filters */}
          <div className="search-filters">
            <input
              type="text"
              placeholder="Search by Name"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Search by Email"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
            />
            <input
              type="date"
              placeholder="Search by Date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
            />
          </div>
          <div className="w-full overflow-hidden border border-gray-200 rounded-lg shadow-sm bg-white">
            <div className="w-full overflow-x-auto">
              <table className="min-w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-gray-600">Name</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Email</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Joined On</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Mobile</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Current/previous paid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr
                      key={user._id}
                      onClick={() => handleUserClick(user)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-900 font-medium">
                        <div className="flex items-center gap-2">
                          {user.name}
                          {renderStatusIcon(user.status)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-900">{user.email}</td>
                      <td className="px-4 py-3 text-gray-900">{user.joiningDate}</td>
                      <td className="px-4 py-3 text-gray-900">{user.mobile}</td>
                      <td className="px-4 py-3 text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.subscription
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {user.subscription ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <p>It&apos;s empty</p>
      )}

      {selectedUser && (
        <div className="user-details-adminuser">
          {/* Top-right X close icon */}
          <button
            onClick={() => setSelectedUser(null)}
            className="admin-user-close-x"
            aria-label="Close"
          >
            <X size={22} />
          </button>

          {/* Action buttons row */}
          <div className="admin-user-actions">
            {/* Blacklist / Whitelist */}
            {selectedUser.status === 'blacklisted' ? (
              <button
                onClick={() => handleWhitelistClick(selectedUser._id)}
                className="admin-btn-whitelist"
              >
                Whitelist user
              </button>
            ) : (
              <button
                onClick={() => handleBlockClick(selectedUser._id)}
                className="admin-btn-blacklist"
              >
                Blacklist user
              </button>
            )}

            {/* Schedule Deletion (48H) — only for active/non-blacklisted users */}
            {selectedUser.status !== 'blacklisted' && selectedUser.status !== 'pending_deletion' && (
              <button
                onClick={() => handleScheduleDeleteClick(selectedUser._id)}
                className="admin-btn-schedule-delete"
              >
                <Clock size={14} />
                Schedule Deletion (48H)
              </button>
            )}

            {/* Force Delete — available for all user states */}
            {selectedUser.status !== 'blacklisted' && (
              <button
                onClick={() => handleForceDeleteClick(selectedUser._id)}
                className="admin-btn-force-delete"
              >
                <Trash2 size={14} />
                Force Delete
              </button>
            )}

            {/* Gift Subscription — only for non-blacklisted users */}
            {selectedUser.status !== 'blacklisted' && giftSubStep === null && (
              <button
                onClick={handleGiftSubOpen}
                className="admin-btn-gift-sub"
              >
                <Gift size={14} />
                {selectedUser.subscription ? ' Extend Subscription' : 'Gift Subscription'}
              </button>
            )}

            {/* Revoke Subscription — only if user has active subscription */}
            {selectedUser.subscription && giftSubStep === null && (
              <button
                onClick={handleRevokeSubscription}
                className="admin-btn-force-delete"
              >
                <XCircle size={14} />
                Revoke Subscription
              </button>
            )}
          </div>

          {/* Gift Subscription Section */}
          {renderGiftSubSection()}

          {/* Pending deletion banner with live countdown */}
          {selectedUser.status === 'pending_deletion' && (
            <div className="admin-pending-deletion-banner">
              <Clock size={16} />
              <span>
                {deletionTimeRemaining && !deletionTimeRemaining.expired ? (
                  <>This account is scheduled for deletion. <strong>
                    {deletionTimeRemaining.hours}h {deletionTimeRemaining.minutes}m {deletionTimeRemaining.seconds}s
                  </strong> remaining before permanent removal.  If the user logs back in during this time, the deletion will be automatically canceled and their account will be fully restored.</>
                ) : deletionTimeRemaining && deletionTimeRemaining.expired ? (
                  <>Deletion grace period has expired. Account will be removed on next cleanup cycle.</>
                ) : (
                  <>This account is scheduled for deletion.</>
                )}
              </span>
            </div>
          )}

          <img src={selectedUser.image} alt="profile" />
          <p>Bio: {selectedUser.bio}</p>
          <p>Joined On: {selectedUser.joiningDate}</p>
        </div>
      )}
    </div>
  );
};

export default Users;
